import React, { type ReactNode } from 'react';

import type { SupportedRepubName } from './types';

export type FAQConfig = {
  productName: string;
  questionsAndAnswers: {
    content: (linkStyleCssClassName: string, scrollToPlans?: () => void) => ReactNode[];
    heading: string;
  }[];
  subtitle: string;
  title: string;
};
export const FAQ_CONFIGS = {
  GZ: {
    productName: 'Gazzetta+',
    title: 'Domande Frequenti',
    subtitle: 'Le risposte alle domande più frequenti su Gazzetta Ai Predictor',
    questionsAndAnswers: [
      {
        heading: 'Come vengono creati i pronostici della sezione Premium Tipster?',
        content: _linkStyleCssClassName => [
          <>
            I pronostici vengono generati dai nostri Tipster basati sull'Intelligenza Artificiale, che elaborano milioni
            di proiezioni per individuare le scommesse con il miglior Expected Value. Ogni Tipster AI analizza e
            seleziona solo le quote matematicamente più vantaggiose nel proprio mercato di specializzazione: nella
            sezione ne troverai di diversi, dai maggiori campionati di calcio fino agli sport americani
          </>,
        ],
      },
      {
        heading: 'Usando Gazzetta Ai Predictor vincerò di più?',
        content: _linkStyleCssClassName => [
          <>
            Non esistono garanzie di vincita. Con AI Predictor hai a disposizione algoritmi di IA che individuano le
            scommesse ad alto valore positivo, offrendoti un vantaggio rispetto a chi si affida solo all'istinto.
            Inoltre, grazie ai Trend puoi scoprire le opportunità con maggior potenziale a lungo termine.
          </>,
        ],
      },
      {
        heading: 'Perché dovrei fidarmi di questo prodotto?',
        content: _linkStyleCssClassName => [
          <>
            Questo servizio nasce dalla collaborazione de La Gazzetta dello Sport con un provider che da oltre 25 anni è
            un punto di riferimento internazionale nel mondo delle statistiche e della comparazione quote. L’esperienza
            maturata, unita a modelli di intelligenza artificiale di ultima generazione e a una base dati enorme,
            garantisce analisi chiare, affidabili e di qualità.
          </>,
          <>
            Se hai ancora dubbi, attiva una prova gratuita di 7 giorni attraverso l'abbonamento annuale per testarlo in
            prima persona. Per qualsiasi domanda, siamo a disposizione via e-mail o tramite il modulo di supporto.
          </>,
        ],
      },
      {
        heading: 'Come faccio a disdire l’abbonamento a Gazzetta Ai Predictor?',
        content: _linkStyleCssClassName => [
          <>
            Se non sei soddisfatto, puoi annullare la prova gratuita dopo 7 giorni senza alcun costo aggiuntivo. E se
            hai un abbonamento già attivo, lo puoi disdire dalla tua pagina personale
          </>,
        ],
      },
      {
        heading: 'Perché non riesco a cliccare una quota?',
        content: _linkStyleCssClassName => [
          <>
            Alcune quote potrebbero risultare non cliccabili se in quel momento non è disponibile una comparazione tra
            più operatori. Tutte le scommesse offerte da due o più operatori saranno regolarmente cliccabili
          </>,
        ],
      },
    ],
  },
  OC: {
    productName: 'oddschecker+',
    title: 'FAQs',
    subtitle: 'Discover answers to frequently asked questions about oddschecker+',
    questionsAndAnswers: [
      {
        heading: 'Can I try out oddschecker+ before committing?',
        content: (linkStyleCssClassName: string) => [
          <>
            Absolutely! You can sign up <span className={linkStyleCssClassName}>here</span>
            {` for a 7-day free trial`}. You will have full access to our full suite of cutting-edge tools and metrics
            designed to help you find long-term profitability.
          </>,
        ],
      },
      {
        heading: 'By using oddschecker+, am I guaranteed to win more?',
        content: (_linkStyleCssClassName: string) => [
          <>
            While nothing is a guarantee, our tools leverage our proprietary AI algorithms to identify bets with
            positive expected value, giving you an edge over the sportsbooks. Additionally, you can utilize Public
            Betting Splits and trends to spot opportunities with long-term returns.
          </>,
        ],
      },
      {
        heading: 'Why should I trust this product?',
        content: (linkStyleCssClassName: string) => [
          <>
            Oddschecker has a 25-plus year history as the global leader in providing value for sports bettors. Our
            state-of-the-art AI models, combined with our extensive experience and data, ensure you get the best value.
            Beyond that, the proof is with our users – check out social media, App Store reviews, and testimonials to
            help inform your decision.
          </>,
          <>
            If you're still not convinced, try us out for free for 7 days and see for yourself. If you have any
            questions, feel free to reach out to us at <b>assist@oddschecker.com</b> or through our contact form{' '}
            <a className={linkStyleCssClassName} href="https://oddscheckerus.zendesk.com/hc/en-us/requests/new">
              here
            </a>
            . We're on your team. Always.
          </>,
        ],
      },
      {
        heading: 'How do I cancel my oddschecker+ subscription?',
        content: (_linkStyleCssClassName: string) => [
          <>
            If you are not fully satisfied, you can cancel your free trial within 7 days at no charge. You can also
            cancel your subscription at any point to stop renewals.
          </>,
        ],
      },
    ],
  },
  US: {
    productName: 'oddschecker+',
    title: 'FAQs',
    subtitle: 'Discover answers to frequently asked questions about oddschecker+',
    questionsAndAnswers: [
      {
        heading: 'Can I try out oddschecker+ before committing?',
        content: (linkStyleCssClassName: string) => [
          <>
            Absolutely! You can sign up <span className={linkStyleCssClassName}>here</span>
            {` for a 7-day free trial`}. You will have full access to our full suite of cutting-edge tools and metrics
            designed to help you find long-term profitability.
          </>,
        ],
      },
      {
        heading: 'By using oddschecker+, am I guaranteed to win more?',
        content: (_linkStyleCssClassName: string) => [
          <>
            While nothing is a guarantee, our tools leverage our proprietary AI algorithms to identify bets with
            positive expected value, giving you an edge over the sportsbooks. Additionally, you can utilize Public
            Betting Splits and trends to spot opportunities with long-term returns.
          </>,
        ],
      },
      {
        heading: 'Why should I trust this product?',
        content: (linkStyleCssClassName: string) => [
          <>
            Oddschecker has a 25-plus year history as the global leader in providing value for sports bettors. Our
            state-of-the-art AI models, combined with our extensive experience and data, ensure you get the best value.
            Beyond that, the proof is with our users – check out social media, App Store reviews, and testimonials to
            help inform your decision.
          </>,
          <>
            If you're still not convinced, try us out for free for 7 days and see for yourself. If you have any
            questions, feel free to reach out to us at <b>assist@oddschecker.com</b> or through our contact form{' '}
            <a className={linkStyleCssClassName} href="https://oddscheckerus.zendesk.com/hc/en-us/requests/new">
              here
            </a>
            . We're on your team. Always.
          </>,
        ],
      },
      {
        heading: 'How do I cancel my oddschecker+ subscription?',
        content: (_linkStyleCssClassName: string) => [
          <>
            If you are not fully satisfied, you can cancel your free trial within 7 days at no charge. You can also
            cancel your subscription at any point to stop renewals.
          </>,
        ],
      },
    ],
  },
  WS: {
    productName: 'WhoScored+',
    title: 'FAQs',
    subtitle: 'Discover answers to frequently asked questions about WhoScored+',
    questionsAndAnswers: [
      {
        heading: 'Can I Try Out WhoScored+ before Committing?',
        content: () => [
          <>
            Absolutely! You can sign up{` for a 7-day free trial`}. You will have full access to our full suite of
            cutting-edge tools and metrics designed to help you find long-term profitability.
          </>,
        ],
      },
      {
        heading: 'By Using WhoScored+, am I guaranteed to win more?',
        content: _linkStyleCssClassName => [
          <>
            While nothing is a guarantee, our tools leverage our proprietary AI algorithms to identify bets with
            positive expected value, giving you an edge over the sportsbooks.
          </>,
        ],
      },
      {
        heading: 'Why Should I Trust This Product?',
        content: linkStyleCssClassName => [
          <>
            Our state-of-the-art AI models, combined with our extensive experience and data, ensure you get the best
            value. Beyond that, the proof is with our users – check out social media, App Store reviews, and
            testimonials to help inform your decision.
          </>,
          <>
            If you're still not convinced, try us out for free for 7 days and see for yourself. If you have any
            questions, feel free to reach out to us at{' '}
            <a className={linkStyleCssClassName} href="mailto:support@whoscored.com">
              support@whoscored.com
            </a>
            . We're on your team. Always.
          </>,
        ],
      },
      {
        heading: 'How do I cancel?',
        content: _linkStyleCssClassName => [
          <>
            If you are not fully satisfied, you can cancel your free trial within 7 days at no charge. You can also
            cancel your subscription at any point to stop renewals.
          </>,
        ],
      },
    ],
  },
} satisfies Record<SupportedRepubName, FAQConfig>;
